import data from "../../shared/sharedData";

export default {
  name: "Employer Information",
  prefix: "C",
  sections: [
    {
      fields: [
        {
          widget: "inlinePrefiller",
          prefillLabel:
            "Select an Employer profile to populate this section (optional)",
          prefill: {
            schema: "employerProfiles",
            type: "employer",
            formName: "9141",
            formSection: "C",
          },
        },
        {
          code: "C.1",
          machine_name: "empBusinessName",
          widget: "text",
          label: "Legal business name",
          validate: ["required", "noNAs"],
          maxLength: 120,
          tooltip: `Enter the full legal name of the business, person, association, firm, corporation, or organization, i.e., the employer filing this application. The employer’s full legal name is the exact name of the individual, corporation, LLC, partnership, or other organization that is reported to the Internal Revenue Service (IRS).`,
        },
        {
          code: "C.2",
          machine_name: "empTradeName",
          widget: "text",
          label: "Trade name/Doing Business As (DBA), if applicable §",
          maxLength: 120,
          tooltip: `Enter the full trade name or “Doing Business As” (DBA) name, if applicable, of the business, person, association, firm, corporation, or organization, i.e., the employer filing this application.`,
        },
        {
          code: "C.3",
          machine_name: "empAddr1",
          widget: "text",
          label: "Address 1",
          validate: [
            "required",
            {
              minLength: 2,
              message: "Address 1 must contain at least 2 characters.",
            },
            "noNAs",
          ],
          tooltip: `Enter the street address of the employer’s principal place of business. The place of business must be a physical location and not a Post Office (P.O.) Box.`,
          maxLength: 60,
        },
        {
          code: "C.4",
          machine_name: "empAddr2",
          widget: "text",
          label: "Address 2",
          maxLength: 60,
          tooltip: `If additional space is needed for the street address, use this line to complete the employer’s street address. If no additional space is needed, enter “N/A” or leave blank.`,
        },
        {
          code: "C.8",
          label: "Country",
          machine_name: "empCountry",
          widget: "select",
          selection: data.country,
          validate: ["required"],
          clearSchema: ["empState"],
          tooltip: `Enter the country of the employer’s principal place of business. If the city and country are the same, the name must still be entered in both fields.`,
        },
        {
          code: "C.5",
          machine_name: "empCity",
          widget: "text",
          label: "City",
          disabled: true,
          disableWhenField: "empCountry",
          validate: [
            "required",
            {
              minLength: 2,
              message: "City must contain at least 2 characters.",
            },
            "noNAs",
          ],
          tooltip: `Enter the city of the employer’s principal place of business. If the city and country are the same, the name must still be entered in both fields.`,
          maxLength: 35,
        },
        {
          code: "C.6",
          label: "State §",
          machine_name: "empState",
          widget: "select",
          selection: data.state,
          state: true,
          maxLength: 35,
          disabled: true,
          disableWhenField: "empCountry",
          isVisible: {
            when: "empCountry",
            is: ["UNITED STATES OF AMERICA", "- Select -", ""],
            operator: "||",
            hideStyles: true,
          },
          validate: [
            {
              isRequired: "empCountry",
              message: "This field is required",
              needsFieldValues: true,
              extraData: {
                valueOf: "UNITED STATES OF AMERICA",
              },
            },
          ],
          tooltip: `Enter the state of the employer’s principal place of business. State is required if the country is United States.`,
        },
        {
          code: "C.7",
          machine_name: "empPostcode",
          widget: "text",
          label: "Postal code",
          disabled: true,
          disableWhenField: "empCountry",
          validate: [
            //"postal",
            {
              postalCountry: "empCountry",
              needsFieldValues: true,
              extraData: {
                valueOf: "empCountry",
              },
            },
            {
              isRequired: "empCountry",
              message: "This field is required",
              needsFieldValues: true,
              extraData: {
                valueOf: "UNITED STATES OF AMERICA",
              },
            },
          ],
          tooltip: `Enter the postal (ZIP) code of the employer’s principal place of business.`,
          maxLength: 10,
        },
        {
          code: "C.9",
          machine_name: "empProvince",
          widget: "text",
          label: "Province (if applicable) §",
          disabled: true,
          disableWhenField: "empCountry",
          validate: [
            {
              isRequired: "empCountry",
              message: "This field is required",
              needsFieldValues: true,
              extraData: {
                valueOf: "CANADA",
              },
            },
          ],
          tooltip: `Enter the province of the employer’s principal place of business, if applicable.`,
          maxLength: 25,
        },
        {
          code: "C.10",
          label: "Telephone number",
          machine_name: "empPhone",
          widget: "phone",
          validate: ["required", "phone"],
          tooltip: `Enter the area code and telephone number for the employer’s principal place of business. Include country code, if applicable.`,
        },
        {
          code: "C.11",
          label: "Extension (if applicable) §",
          machine_name: "empPhoneext",
          widget: "text",
          maxLength: 4,
          tooltip: `Enter the extension of the telephone number for the employer’s principal place of business, if applicable.`,
        },
        {
          code: "C.12",
          label: "Federal Employer Identification Number (FEIN from IRS) ",
          machine_name: "empFein",
          widget: "text",
          validate: [
            "required",
            "fein",
            { minLength: 10, message: "This is not a valid FEIN" },
            {
              FEINPrefixValidator: "empFein",
              needsFieldValues: true,
              message: "This is not a valid FEIN",
            },
          ],
          tooltip: `Enter the nine-digit Federal Employer identification Number (FEIN) as assigned by the IRS. Do not enter a social security number.
          <br><b>Note:</b> All employers, including private households, MUST obtain an FEIN from the IRS before completing this application. Information on obtaining an FEIN can be found at www.IRS.gov.`,
          parse: "fein",
          maxLength: 10,
        },
        {
          code: "C.13",
          machine_name: "empNaicsDesc",
          widget: "text",
          label: "NAICS Description",
          hidden: true,
        },
        {
          code: "C.13",
          machine_name: "empNaics",
          widget: "naics",
          label: "NAICS code",
          field_to_modify: "empNaicsDesc",
          validate: ["required"],
          tooltip: `Enter the four to six-digit North American Industry Classification System (NAICS) code that best describes the employer’s business, not the specific job opportunity being requested for temporary employment certification. A listing of NAICS codes can be found at <a href="https://www.census.gov/naics/" rel="noopener noreferrer" target="_blank">https://www.census.gov/naics/</a>.`,
          //tooltip: `<td onClick={() window.open("https://www.census.gov/naics/", "_blank")}>https://www.census.gov/naics/</td>.`,
        },
      ],
    },
  ],
};
